import { useSearchParams, Link } from 'react-router-dom';
import { formatMoney } from '@teentack/shared';
import { ParentShell } from '@/features/parent/ParentShell';
import { EmptyState } from '@/components/ui/Feedback';
import { Skeleton } from '@/components/ui/Skeleton';
import { useParentOverview } from '@/features/parent/use-parent-overview';
import { useShortlist } from '@/features/shortlist/use-shortlist';
import { Badge } from '@/components/ui/Badge';
import { SelectInput } from '@/components/ui/Input';
import { ErrorNote } from '@/components/Form';
import { BookIcon, MapPinIcon, WalletIcon } from '@/components/ui/Icons';
import { relativeTime } from '@/lib/relative-time';
import { presentApiError } from '@/lib/form-errors';

/**
 * What a teen is actually looking at. [Parent step 7]
 *
 * The point of this page is that it is not the guardian's own list: it shows
 * everything the teen has saved, including jobs the guardian has never seen,
 * which is the difference between suggesting work and understanding what they
 * are drawn to.
 */
export function TeenShortlistPage() {
  const [params, setParams] = useSearchParams();
  const overview = useParentOverview();

  const teens = (overview.data?.teens ?? []).filter((teen) => teen.verified);
  // Default to the first approved teen, so the page is never empty by accident.
  const teenId = params.get('teenId') ?? teens[0]?.id ?? '';

  const shortlist = useShortlist({ teenId, pageSize: 50 }, Boolean(teenId));
  const items = shortlist.data?.items ?? [];
  const activeTeen = teens.find((teen) => teen.id === teenId);

  function setTeenId(next: string): void {
    const updated = new URLSearchParams(params);
    if (next) updated.set('teenId', next);
    else updated.delete('teenId');
    setParams(updated, { replace: true });
  }

  return (
    <ParentShell
      title="What they are looking at"
      description="Every job your teen has saved, and the ones you have sent them."
    >
      {teens.length === 0 && (
        <EmptyState icon={<BookIcon className="size-6" />} title="No approved teens yet">
          Once you approve a teen account, what they save shows up here.
        </EmptyState>
      )}

      {teens.length > 1 && (
        <div className="mb-6 max-w-xs">
          <SelectInput
            label="Teen"
            value={teenId}
            onChange={(event) => setTeenId(event.target.value)}
          >
            {teens.map((teen) => (
              <option key={teen.id} value={teen.id}>
                {teen.name}
              </option>
            ))}
          </SelectInput>
        </div>
      )}

      {shortlist.isError && (
        <ErrorNote
          message={presentApiError(shortlist.error, 'Could not load that shortlist').message}
        />
      )}

      {teenId && shortlist.isPending && (
        <div className="space-y-4" aria-busy="true">
          <Skeleton className="h-24" />
          <Skeleton className="h-24" />
        </div>
      )}

      {teenId && shortlist.data && items.length === 0 && (
        <EmptyState icon={<BookIcon className="size-6" />} title="Nothing saved yet">
          {activeTeen?.name.split(' ')[0] ?? 'Your teen'} has not saved any jobs. You can browse
          and send them one from any job page.
        </EmptyState>
      )}

      {items.length > 0 && (
        <ul className="space-y-4">
          {items.map((saved) => (
            <li
              key={saved.id}
              className="rounded-2xl border border-line bg-white p-5 shadow-soft transition hover:border-brand-200"
            >
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="min-w-0">
                  <Link
                    to={`/jobs/${saved.job.id}`}
                    className="text-base font-bold text-ink hover:underline"
                  >
                    {saved.job.title}
                  </Link>
                  <p className="mt-1 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-slate-500">
                    <span>{saved.job.posterName}</span>
                    <span className="inline-flex items-center gap-1 font-bold text-ink">
                      <WalletIcon className="size-3.5 text-safe-600" />
                      {formatMoney(saved.job.pay.amountMinor, saved.job.pay.currency)}
                    </span>
                    {saved.job.location && (
                      <span className="inline-flex items-center gap-1">
                        <MapPinIcon className="size-3.5 text-slate-400" />
                        {saved.job.location}
                      </span>
                    )}
                  </p>
                </div>

                <div className="flex shrink-0 flex-col items-end gap-1.5">
                  {/* "Saved by you" is the honest label when the guardian sent it. */}
                  <Badge tone={saved.savedByName ? 'accent' : 'neutral'}>
                    {saved.savedByName ? `Sent by ${saved.savedByName}` : 'Saved by them'}
                  </Badge>

                  {/* Only meaningful for something that was sent: a teen does
                      not "open" a job they saved themselves. [Parent step 7] */}
                  {saved.savedByName && (
                    <Badge tone={saved.openedAt ? 'safe' : 'sun'}>
                      {saved.openedAt ? 'They opened it' : 'Not opened yet'}
                    </Badge>
                  )}
                </div>
              </div>

              <p className="mt-3 border-t border-line pt-3 text-xs text-slate-400">
                Saved {relativeTime(saved.savedAt)}
                {saved.openedAt && ` · opened ${relativeTime(saved.openedAt)}`}
              </p>
            </li>
          ))}
        </ul>
      )}
    </ParentShell>
  );
}
